import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FiArrowLeft, FiSave, FiCheckCircle, FiFileText } from "react-icons/fi";
import Swal from "sweetalert2";

const CreateInvoice = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [poData, setPoData] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split("T")[0]);
  const [poList, setPoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const fetchPOList = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order?status=approved`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      setPoList(result.data || []);
    } catch (error) {
      console.error("Error fetching PO list:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPOData = async (poNumber) => {
    try {
      const encodedPoNumber = encodeURIComponent(poNumber);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodedPoNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch PO data. Status: ${response.status}`);
      }

      const result = await response.json();
      setPoData(result.data);
      if (result.data) {
        setAmount(result.data.total_amount || 0);
        setDescription(`Pembayaran Invoice ${result.data.po_number || result.data.number || ""}`);
      }
    } catch (error) {
      console.error("Error fetching PO data:", error);
    }
  };

  useEffect(() => {
    fetchPOList();
  }, []);

  const handleSave = async () => {
    if (!invoiceNumber || !poData || !dueDate) {
      Swal.fire({
        icon: "warning",
        title: "Form Belum Lengkap",
        text: "Silakan isi Nomor Invoice, pilih Purchase Order, dan tentukan Tanggal Jatuh Tempo!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setLoadingSubmit(true);
    try {
      const payload = {
        id_po: poData?.id || 0,
        number: invoiceNumber,
        amount: Number(amount),
        description: description || `Pembayaran ${poData?.po_number || poData?.number} #1`,
        due_date: dueDate,
        paid_date: paidDate,
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/invoice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit invoice. Status: ${response.status}`);
      }

      await Swal.fire({
        title: "Invoice Berhasil Disimpan!",
        text: `Invoice #${invoiceNumber} telah dibuat.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      navigate("/inventory/invoice");
    } catch (error) {
      console.error("Error saving invoice:", error);
      Swal.fire({
        title: "Gagal Menyimpan",
        text: "Terjadi kesalahan saat membuat invoice.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inventory/invoice")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Buat Invoice Tagihan
            </h1>
            <p className="text-xs text-gray-500">
              Pilih Purchase Order yang telah disetujui (Approved) dan buat tagihan invoice
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Form Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Form Inputs */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5"
        >
          <h2 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
            <FaFileInvoiceDollar className="text-emerald-600 w-5 h-5" />
            <span>Form Data Invoice</span>
          </h2>

          <div className="space-y-4 text-xs">
            {/* Select PO */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Pilih Nomor Purchase Order (Approved)
              </label>
              <select
                onChange={(e) => fetchPOData(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="">-- Pilih Purchase Order --</option>
                {poList.map((po) => (
                  <option key={po.id} value={po.po_number || po.number}>
                    {po.po_number || po.number} - {po.distributor?.name || "Distributor"}
                  </option>
                ))}
              </select>
            </div>

            {/* Invoice Number */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Nomor Invoice Penagihan
              </label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Contoh: INV/2026/08/001"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            {/* Amount */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Nominal Tagihan (Rp)
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Tanggal Jatuh Tempo
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Tanggal Dibayar
                </label>
                <input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Keterangan / Catatan
              </label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Catatan pembayaran invoice..."
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              onClick={() => navigate("/inventory/invoice")}
              className="px-4 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={loadingSubmit}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{loadingSubmit ? "Menyimpan..." : "Simpan Invoice"}</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Right Column: PO Info Preview */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
        >
          <h2 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3">
            Preview Detail Purchase Order
          </h2>

          {poData ? (
            <div className="space-y-3 text-xs">
              <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-100 space-y-1">
                <span className="text-gray-400 font-bold uppercase text-[10px]">Nomor PO</span>
                <p className="font-extrabold text-emerald-800 text-sm font-mono">
                  {poData.po_number || poData.number}
                </p>
              </div>

              <div className="space-y-2 pt-2 text-gray-600">
                <div className="flex justify-between">
                  <span className="text-gray-400 font-semibold">Distributor PBF:</span>
                  <span className="font-bold text-gray-800">{poData.distributor?.name || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 font-semibold">Outlet Tujuan:</span>
                  <span className="font-bold text-gray-800">{poData.outlet?.name || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 font-semibold">Status PO:</span>
                  <span className="font-bold text-teal-700 capitalize">{poData.status}</span>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-gray-400 font-bold">Total Nominal PO</span>
                <span className="text-base font-extrabold text-emerald-700 font-mono">
                  Rp {(poData.total_amount || 0).toLocaleString("id-ID")}
                </span>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-gray-400 text-xs font-semibold">
              <FiFileText className="w-10 h-10 mx-auto mb-2 text-gray-300 stroke-1" />
              Pilih Purchase Order terlebih dahulu untuk melihat ringkasan data tagihan.
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CreateInvoice;
